import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import Link from "next/link";
import { geistSans } from "../buttons/Button";
import { cn } from "@/lib/utils";

export type DropdownProps = {
  links: { link: string; label: string }[];
  defaultOpen: boolean;
  label?: string;
  children?: React.ReactNode;
  className?: string;
  dropdownContentAlign?: "start" | "end" | "center";
  footerItem?: React.ReactNode;
};

const Dropdown = ({
  links,
  defaultOpen,
  label,
  children,
  className,
  dropdownContentAlign = "start",
  footerItem,
}: DropdownProps) => {
  return (
    <DropdownMenu aria-label={`${label} dropdown menu`} defaultOpen={defaultOpen}>
      <DropdownMenuTrigger
        className={cn(
          "text-dark-primary hover:border-primary-light flex items-center gap-3 rounded-sm border p-1 xl:px-3 xl:py-2.5 text-sm font-medium",
          geistSans.className,
          className
        )}
      >
        {label} {children}
        <ChevronDown
          size={16}
          aria-hidden="true"
          className="chevron text-primary-light transition-transform duration-200 max-xl:hidden"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        role="menu"
        align={dropdownContentAlign}
        className={`mt-1 min-w-[170px] space-y-1 border border-gray-300 p-1.5 pt-3 pb-1.5 ${geistSans.className}`}
      >
        {links.map((link, index) => (
          <DropdownMenuItem
            className={cn(
              "text-dark-primary flex w-full cursor-pointer flex-col items-start p-0 px-2 py-1 text-sm transition-all duration-200 hover:scale-[102%] hover:bg-zinc-100",
              dropdownContentAlign === "end" && "items-end",
              dropdownContentAlign === "center" && "items-center"
            )}
            key={index}
            asChild
            role="menuitem"
          >
            <Link href={link.link} aria-label={`Navigate to ${link.label}`}>
              {link.label}
            </Link>
          </DropdownMenuItem>
        ))}

        {footerItem && (
          <div role="none" className="mt-1 border-t pt-1.5">
            {footerItem}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default Dropdown;
